import {
  ChangeDetectionStrategy,
  Component,
  effect,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { GalleryMapComponent, GalleryMapFootprint, GalleryMapMarker, MapBounds } from '@app/pages/upload-images/gallery-map/gallery-map.component';
import { UploadedGeoImagesStateService } from '../services/uploaded-geo-images-state.service';
import { UploadedGeoImageService } from '../services/uploaded-geo-image.service';
import { UploadedGeoImageListItem } from '../services/uploaded-geo-image.models';
import { boundsOf, LatLng, wktPolygonToLatLngRing } from '../utils/wkt.util';
import { escapeHtml } from '../utils/html.util';

/**
 * Map of the "Uploaded Geo-Images" tab (FR-010): a `Photo` is a marker, a `Raster` is its footprint outline —
 * never both for one image. Panning or zooming reloads the list to what is in view (FR-012); this tab only,
 * the provider-products tab keeps its own map with that auto-refresh off.
 */
@Component({
  selector: 'app-uploaded-geo-images-map',
  standalone: true,
  imports: [CommonModule, GalleryMapComponent],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './uploaded-geo-images-map.component.html',
  styleUrls: ['./uploaded-geo-images-map.component.scss']
})
export class UploadedGeoImagesMapComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('galleryMap') private galleryMap?: GalleryMapComponent;

  /** The tab keeps this map alive while hidden; Leaflet has to be told when it becomes visible again. */
  @Input() visible = true;

  readonly markers = signal<GalleryMapMarker[]>([]);
  readonly footprints = signal<GalleryMapFootprint[]>([]);

  private readonly bounds$ = new Subject<MapBounds>();
  private readonly subscriptions = new Subscription();
  private drawnItems: UploadedGeoImageListItem[] | null = null;
  private drawnSelectedId: string | null = null;
  private focusedId: string | null = null;

  constructor(
    private readonly state: UploadedGeoImagesStateService,
    private readonly api: UploadedGeoImageService,
    private readonly datePipe: DatePipe
  ) {
    // Replaces a `changed$` subscription: reading these signals here makes the effect re-run whenever any of
    // them changes, with no manual markForCheck (the map's own template reads `markers()`/`footprints()`).
    effect(() => this.redraw(this.state.items(), this.state.selectedId(), this.state.selectionOrigin()));
    effect(() => this.focusSelection(this.state.selectedItem(), this.state.selectionOrigin()));
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.bounds$
        .pipe(
          debounceTime(400),
          // The tab keeps this map alive while hidden. A hidden container measures 0x0, so Leaflet then reports a
          // point-sized viewport that would reload the gallery with an empty result.
          filter(bounds => this.isUsableViewport(bounds))
        )
        .subscribe(bounds => this.state.onMapBoundsChange(bounds))
    );

    this.subscriptions.add(
      this.state.fitRequested$.subscribe(bounds => {
        // No animation: this fit follows a fresh load (tab open, Apply/Reset, upload) with no prior view worth
        // easing from, and an animated fit's requestAnimationFrame steps can stall indefinitely in a backgrounded
        // tab, leaving the map — and the viewport-bounded reload its 'moveend' triggers — stuck on the old view.
        this.galleryMap?.fitBounds(bounds, 16, false);
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible && !changes['visible'].firstChange) {
      setTimeout(() => this.galleryMap?.invalidateSize(), 0);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** Called by the page after its layout changed size (e.g. the filter sidebar was collapsed). */
  refreshSize(): void {
    this.galleryMap?.invalidateSize();
  }

  onBoundsChange(bounds: MapBounds): void {
    this.bounds$.next(bounds);
  }

  onItemClick(id: string): void {
    this.state.select(id, 'map');
  }

  /** A viewport is usable when the map is on screen and has a real extent. */
  private isUsableViewport(bounds: MapBounds): boolean {
    return (
      this.visible &&
      Number.isFinite(bounds.north) &&
      Number.isFinite(bounds.south) &&
      Number.isFinite(bounds.east) &&
      Number.isFinite(bounds.west) &&
      bounds.north > bounds.south &&
      bounds.east > bounds.west
    );
  }

  private redraw(items: UploadedGeoImageListItem[], selectedId: string | null, selectionOrigin: 'list' | 'map'): void {
    const itemsChanged = this.drawnItems !== items;
    if (!itemsChanged && this.drawnSelectedId === selectedId) {
      return;
    }

    this.drawnItems = items;
    this.drawnSelectedId = selectedId;

    // Rebuilding the markers clears the map's marker layer, which closes the popup the user just opened by
    // clicking one. So a selection made on the map leaves the markers alone; only a new result set or a pick in
    // the list restyles them. Fresh arrays are what tells the OnPush map to redraw.
    if (itemsChanged || selectionOrigin === 'list') {
      this.markers.set(
        items
          .filter(item => item.latitude != null && item.longitude != null)
          .map(item => ({
            id: item.id,
            lat: item.latitude!,
            lng: item.longitude!,
            title: item.title,
            popup: this.buildPopup(item),
            isSelected: item.id === selectedId
          }))
      );
    }

    this.footprints.set(
      items
        .map(item => ({ item, ring: wktPolygonToLatLngRing(item.footprintWkt) }))
        .filter(entry => entry.ring.length > 0)
        .map(({ item, ring }) => ({
          id: item.id,
          coordinates: ring,
          title: item.title,
          isSelected: item.id === selectedId
        }))
    );
  }

  /** Zooms to an image the user just picked in the list; a pick on the map itself never moves the map. */
  private focusSelection(selected: UploadedGeoImageListItem | null, selectionOrigin: 'list' | 'map'): void {
    if (!selected) {
      this.focusedId = null;
      return;
    }
    if (selected.id === this.focusedId) {
      return;
    }
    this.focusedId = selected.id;
    if (selectionOrigin !== 'list') {
      return;
    }

    const points: LatLng[] =
      selected.latitude != null && selected.longitude != null
        ? [[selected.latitude, selected.longitude]]
        : wktPolygonToLatLngRing(selected.footprintWkt);
    const bounds = boundsOf(points);
    if (bounds) {
      this.galleryMap?.fitBounds(bounds, 15);
    }
  }

  /** Popup markup is an HTML string, so everything user-supplied goes through {@link escapeHtml}. */
  private buildPopup(item: UploadedGeoImageListItem): string {
    const date = item.captureDateUtc ? this.datePipe.transform(item.captureDateUtc, 'dd.MM.yyyy HH:mm') : null;
    const thumbnail = this.api.getThumbnailUrl(item, 240, 160);
    const image = thumbnail
      ? `<img src="${escapeHtml(thumbnail)}" alt="" style="width:100%;height:auto;border-radius:8px;display:block" />`
      : '';
    return `
      <div style="width:200px">
        ${image}
        <div style="margin-top:6px;font-weight:600;word-break:break-word">${escapeHtml(item.title)}</div>
        ${date ? `<div style="font-size:12px;opacity:.7">${escapeHtml(date)}</div>` : ''}
      </div>
    `;
  }
}
